import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Live HUD Small Area FMR lookup from the `safmr_rates` table — the same source
 * and rules the property-search edge function uses, but client-side so the
 * Section 8 module reads current FMRs from the DB instead of a value baked into
 * property_data. `safmr_rates` is static public reference data not present in
 * the generated Supabase types, so it's read through a narrow client cast.
 */

export interface SafmrRow {
  zip_code: string;
  hud_area_name: string;
  safmr_0br: number;
  safmr_1br: number;
  safmr_2br: number;
  safmr_3br: number;
  safmr_4br: number;
}

const SAFMR_COLUMNS = "zip_code,hud_area_name,safmr_0br,safmr_1br,safmr_2br,safmr_3br,safmr_4br";

const refDb = supabase as unknown as {
  from: (t: string) => {
    select: (cols: string) => {
      in: (col: string, vals: string[]) => Promise<{ data: SafmrRow[] | null; error: { message?: string } | null }>;
    };
  };
};

/** 5-digit zero-padded ZIP (strips ZIP+4, restores leading zeros: "602" → "00602"). */
export function normalizeZip(zip: unknown): string {
  const digits = String(zip ?? "").replace(/[^0-9]/g, "").slice(0, 5);
  return digits ? digits.padStart(5, "0") : "";
}

/** HUD rule beyond the published 4BR: 4BR + 15% per extra bedroom. */
function safmrForBedrooms(row: SafmrRow, bedrooms: number): number {
  const beds = Math.max(0, Math.round(Number(bedrooms) || 0));
  const base = [row.safmr_0br, row.safmr_1br, row.safmr_2br, row.safmr_3br, row.safmr_4br];
  if (beds <= 4) return base[beds];
  return Math.round(row.safmr_4br * (1 + 0.15 * (beds - 4)));
}

/** A ZIP can map to several HUD areas; prefer the one whose name matches the city. */
function pickRow(rows: SafmrRow[], city: string): SafmrRow | null {
  if (!rows || rows.length === 0) return null;
  if (rows.length === 1) return rows[0];
  const c = (city ?? "").trim().toLowerCase();
  if (c) {
    const matched = rows.filter((r) => (r.hud_area_name ?? "").toLowerCase().includes(c));
    if (matched.length > 0) return matched[0];
  }
  return rows[0];
}

export function useSafmr(zips: string[]) {
  const normZips = useMemo(
    () => [...new Set(zips.map(normalizeZip).filter(Boolean))].sort(),
    [zips],
  );

  const query = useQuery({
    queryKey: ["safmr-rates", normZips.join(",")],
    enabled: normZips.length > 0,
    staleTime: 1000 * 60 * 60, // static reference data — cache an hour
    queryFn: async () => {
      const { data, error } = await refDb.from("safmr_rates").select(SAFMR_COLUMNS).in("zip_code", normZips);
      if (error) throw new Error(error.message ?? "safmr_rates lookup failed");
      return data ?? [];
    },
  });

  const byZip = useMemo(() => {
    const map = new Map<string, SafmrRow[]>();
    for (const row of query.data ?? []) {
      const arr = map.get(row.zip_code) ?? [];
      arr.push(row);
      map.set(row.zip_code, arr);
    }
    return map;
  }, [query.data]);

  /** FMR for a property's ZIP/city/bedrooms, or null when the ZIP isn't in the table. */
  const getFmr = useCallback(
    (zip: unknown, city: string, bedrooms: number): number | null => {
      const row = pickRow(byZip.get(normalizeZip(zip)) ?? [], city);
      return row ? safmrForBedrooms(row, bedrooms) : null;
    },
    [byZip],
  );

  return { getFmr, isLoading: query.isLoading };
}
